import { cn } from "@/lib/cn";
import { useSession } from "@/components/providers/session-provider";
import { useSidebar } from "@/components/providers/sidebar-provider";
import { Sidebar } from "./sidebar";
import { Topbar } from "./topbar";
import { MarketingHeader } from "./marketing-header";
import { Footer } from "./footer";
import { BottomNav } from "./bottom-nav";
import { ChatDockProvider } from "@/components/chat/chat-dock";

import { useLocation } from "react-router-dom";
import type { ReactNode } from "react";

/** Routes that own the whole viewport and supply their own closing chrome. */
const FULL_BLEED_ROUTES = ["/signin"];

/**
 * Signed-in users get the application shell (collapsible left rail + topbar).
 * Anonymous visitors get the public marketing chrome so the landing page keeps
 * its full-bleed hero.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const { collapsed } = useSidebar();
  const { pathname } = useLocation();
  const authenticated = me?.authenticated === true;
  const fullBleed = FULL_BLEED_ROUTES.includes(pathname);

  if (!authenticated) {
    return (
      <div className="flex min-h-[100dvh] flex-col">
        <MarketingHeader />
        <main id="main" className={fullBleed ? "flex flex-1 flex-col" : "flex-1"}>
          {children}
        </main>
        {!fullBleed && <Footer />}
      </div>
    );
  }

  return (
    <ChatDockProvider>
      <div className="min-h-screen">
        <Sidebar />
        <div
          className={cn(
            "flex min-h-screen flex-col pb-[calc(4.5rem+env(safe-area-inset-bottom))] transition-[padding] duration-300 ease-out lg:pb-0",
            collapsed
              ? "lg:pl-[var(--sidebar-w-collapsed)]"
              : "lg:pl-[var(--sidebar-w)]",
          )}
        >
          <Topbar />
          <main id="main" className="relative flex-1">
            {children}
          </main>
          <Footer />
        </div>
        <BottomNav />
      </div>
    </ChatDockProvider>
  );
}
